import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import carePartnerService from '../../../services/carePartnerService';
import { CARE_PARTNER_TYPES } from '../../../constants/carePartnerTypes';
import Icon from '../../common/Icons';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import CarePartnerListItem from './CarePartnerListItem';
import { getPartnerTypePillProps, PARTNER_TYPE_SHORT } from './carePartnerListDisplay';
import '../HeadDoctorDashboard.css';

const EMPTY_FORM = {
  name: '',
  partner_type: CARE_PARTNER_TYPES.HOSPITAL.id,
  address: '',
  phone: '',
  email: '',
  notes: '',
  is_active: true
};

export default function CarePartnersManager() {
  const copy = HEAD_DOCTOR_COPY.PARTNERS;
  const { toast } = useDoctorWorkspace();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterType) params.partner_type = filterType;
      if (search.trim()) params.search = search.trim();
      if (!showInactive) params.is_active = 'true';

      const res = await carePartnerService.list(params);
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [filterType, search, showInactive]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const stats = useMemo(
    () => ({
      count: items.length,
      active: items.filter((i) => i.is_active).length,
      inactive: items.filter((i) => !i.is_active).length
    }),
    [items]
  );

  const hasActiveFilters =
    Boolean(searchInput.trim()) || Boolean(filterType) || showInactive;

  const resetFilters = () => {
    setSearchInput('');
    setFilterType('');
    setShowInactive(false);
  };

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 220,
        menuHeight: estimateActionMenuHeight(2, { withSeparator: true })
      })
    );
    setActionMenuItem(item);
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const openCreate = () => {
    closeActionMenu();
    resetForm();
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      partner_type: item.partner_type || CARE_PARTNER_TYPES.HOSPITAL.id,
      address: item.address || '',
      phone: item.phone || '',
      email: item.email || '',
      notes: item.notes || '',
      is_active: Boolean(item.is_active)
    });
    setModalError('');
    setShowModal(true);
    closeActionMenu();
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError('');
    resetForm();
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        notes: form.notes.trim()
      };
      if (!payload.name) throw new Error('Le nom est obligatoire.');

      if (editingId) {
        await carePartnerService.update(editingId, payload);
        toast('success', 'Partenaire mis à jour.');
      } else {
        await carePartnerService.create(payload);
        toast('success', 'Partenaire ajouté.');
      }
      closeModal();
      await loadItems();
    } catch (err) {
      setModalError(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer le partenaire « ${item.name} » ?`)) return;
    closeActionMenu();
    try {
      await carePartnerService.remove(item.id);
      toast('success', 'Partenaire supprimé.');
      if (editingId === item.id) closeModal();
      await loadItems();
    } catch (err) {
      setError(err.message || 'Suppression impossible');
    }
  };

  const isEditing = Boolean(editingId);
  const showPreview = Boolean(form.name.trim());
  const previewType = getPartnerTypePillProps(form.partner_type);

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-partner-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={copy.PAGE_TITLE}
        description={copy.PAGE_SUBTITLE}
        actions={
          <button type="button" className="hd-co-create-btn" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <div className="hd-co-shell">
        <section className="hd-co-stats hd-co-stats--3" aria-label="Synthèse du référentiel">
          <article className="hd-co-stat-card">
            <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.count}</span>
            <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--success">
            <span className="hd-co-stat-card__label">{copy.STAT_ACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.active}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--queue">
            <span className="hd-co-stat-card__label">{copy.STAT_INACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.inactive}</span>
          </article>
        </section>

        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={loadItems}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
            <div className="hd-co-toolbar-meta">
              <strong>{loading ? '—' : stats.count}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="hd-exam-filter-bar">
          <div className="hd-partner-type-bar">
            <span className="hd-partner-type-bar__label" id="hd-partner-type-filter-label">
              {copy.FILTER_TYPE}
            </span>
            <div
              className="hd-partner-type-bar__track"
              role="group"
              aria-labelledby="hd-partner-type-filter-label"
            >
              <button
                type="button"
                className={`hd-partner-type-pill-btn${!filterType ? ' is-active' : ''}`}
                onClick={() => setFilterType('')}
                aria-pressed={!filterType}
              >
                {copy.FILTER_TYPE_ALL}
              </button>
              {Object.values(CARE_PARTNER_TYPES).map((type) => (
                <button
                  key={type.id}
                  type="button"
                  className={`hd-partner-type-pill-btn${
                    filterType === type.id ? ' is-active' : ''
                  }`}
                  onClick={() => setFilterType(type.id)}
                  aria-pressed={filterType === type.id}
                >
                  {PARTNER_TYPE_SHORT[type.id] || type.label}
                </button>
              ))}
            </div>
          </div>
          <div className="hd-exam-filter-bar__end">
            <label className="hd-exam-inactive-toggle">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>{copy.FILTER_INACTIVE}</span>
            </label>
            {hasActiveFilters ? (
              <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                {copy.FILTER_RESET}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="building" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <ul className="hd-partner-list" aria-label={copy.LIST_ARIA}>
            {items.map((item) => (
              <CarePartnerListItem
                key={item.id}
                item={item}
                isEditing={showModal && editingId === item.id}
                copy={copy}
                onOpenMenu={(e) => openActionMenu(e, item)}
              />
            ))}
          </ul>
        )}
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openEdit(actionMenuItem)}
            >
              <Icon name="edit" size={15} />
              <span>{copy.ACTION_EDIT}</span>
            </button>
            <div className="hd-exam-actions-menu__separator" />
            <button
              type="button"
              className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
              role="menuitem"
              onClick={() => remove(actionMenuItem)}
            >
              <Icon name="trash" size={15} />
              <span>{copy.ACTION_DELETE}</span>
            </button>
          </div>
        </>
      ) : null}

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        size="lg"
        className="hd-dis-modal-root hd-partner-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="building" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span
                className={`hd-dis-modal__mode${
                  isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
                }`}
              >
                {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
              </span>
              <h2 className="hd-dis-modal__title">
                {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
              </h2>
              <p className="hd-dis-modal__subtitle mb-0">
                {isEditing ? copy.MODAL_SUBTITLE_EDIT : copy.MODAL_SUBTITLE_NEW}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeModal}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>

        <Form onSubmit={submit}>
          <div className="hd-dis-modal__body">
            {modalError ? (
              <Alert variant="danger" className="hd-dis-modal__alert mb-0">
                {modalError}
              </Alert>
            ) : null}

            <div className="hd-dis-modal__fields">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-partner-name">
                  {copy.FIELD_NAME} *
                </label>
                <input
                  id="hd-partner-name"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={copy.FIELD_NAME_PLACEHOLDER}
                  maxLength={255}
                  required
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-partner-type">
                  {copy.FIELD_TYPE} *
                </label>
                <select
                  id="hd-partner-type"
                  className="hd-dis-modal__input hd-dis-modal__select"
                  value={form.partner_type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, partner_type: e.target.value }))
                  }
                  required
                >
                  {Object.values(CARE_PARTNER_TYPES).map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-dis-modal__label" htmlFor="hd-partner-address">
                  {copy.FIELD_ADDRESS}
                </label>
                <input
                  id="hd-partner-address"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder={copy.FIELD_ADDRESS_PLACEHOLDER}
                  maxLength={500}
                  autoComplete="off"
                />
              </div>

              <div className="hd-dis-modal__field-grid">
                <div className="hd-dis-modal__field">
                  <label className="hd-dis-modal__label" htmlFor="hd-partner-phone">
                    {copy.FIELD_PHONE}
                  </label>
                  <input
                    id="hd-partner-phone"
                    type="tel"
                    className="hd-dis-modal__input"
                    value={form.phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder={copy.FIELD_PHONE_PLACEHOLDER}
                    maxLength={50}
                    autoComplete="off"
                  />
                </div>
                <div className="hd-dis-modal__field">
                  <label className="hd-dis-modal__label" htmlFor="hd-partner-email">
                    {copy.FIELD_EMAIL}
                  </label>
                  <input
                    id="hd-partner-email"
                    type="email"
                    className="hd-dis-modal__input"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder={copy.FIELD_EMAIL_PLACEHOLDER}
                    maxLength={255}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-dis-modal__label" htmlFor="hd-partner-notes">
                  {copy.FIELD_NOTES}
                </label>
                <textarea
                  id="hd-partner-notes"
                  className="hd-dis-modal__textarea"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder={copy.FIELD_NOTES_PLACEHOLDER}
                  maxLength={1000}
                />
              </div>

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-exam-inactive-toggle hd-partner-active-toggle">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                  <span>{copy.FIELD_ACTIVE}</span>
                </label>
              </div>
            </div>

            {showPreview ? (
              <div className="hd-dis-modal__preview hd-partner-modal__preview" aria-live="polite">
                <span className="hd-dis-modal__preview-label">{copy.MODAL_PREVIEW_LABEL}</span>
                <div className="hd-partner-modal__preview-row">
                  <span className="hd-partner-list__title">{form.name.trim()}</span>
                  <span className={previewType.className}>{previewType.label}</span>
                  {form.address.trim() || form.phone.trim() || form.email.trim() ? (
                    <span className="hd-partner-modal__preview-contact">
                      {[form.address.trim(), form.phone.trim(), form.email.trim()]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="hd-dis-modal__footer">
            <button
              type="button"
              className="hd-dis-modal__btn-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              {copy.BTN_CANCEL}
            </button>
            <button type="submit" className="hd-co-create-btn" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {copy.BTN_SAVE}
                </>
              ) : isEditing ? (
                copy.BTN_UPDATE
              ) : (
                <>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.BTN_CREATE}
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
